import React, { FC } from "react";
import { Input } from "@shared/ui/input";
import { DivisionSearchSelect } from "@src/components/business-data-entry";
import { BusinessForm } from "@src/components/business-form";
import { FilterFormPanel } from "@shared/ui/form-panel";
import type { ApiDivisionFilter } from "@src/api/dictionaries/divisions";
import type { WsInplantFilter } from "../../../../../../api/wsInplants";
import { BalanceDocType } from "@src/types/business/balanceDocType";
import type { ListFilterPanelProps } from "../../../../../../shared/listFilterPanel";
import { wsInplantsFilterFormConfig, WsInplantsFilterFormValues } from "./wsInplantsFilter.form-config";
import { useDivisionFilterSeed } from "@app/session-defaults/useDivisionFilterSeed";

/** Пропсы {@link WsInplantsFilterForm}. */
export type WsInplantsFilterFormProps = ListFilterPanelProps<WsInplantFilter> & {
    /** Тип списка (приход / расход) — влияет на доступность подразделений в search-select. */
    listType: BalanceDocType;
};

function divisionFltForSender(listType: BalanceDocType): ApiDivisionFilter | undefined {
    return listType === BalanceDocType.Arrival ? undefined : { AvailableFor: "Create" };
}

function divisionFltForReceiver(listType: BalanceDocType): ApiDivisionFilter | undefined {
    return listType === BalanceDocType.Arrival ? { AvailableFor: "Create" } : undefined;
}

/**
 * Форма фильтра списка ВЗН УП
 */
export const WsInplantsFilterForm: FC<WsInplantsFilterFormProps> = ({ listType, initialFilter, onApply }) => {
    /**
     * Источник подразделения (приход / расход) — влияет на доступность подразделений в search-select.
     *
     * Для прихода - источник подразделения - получатель (receiverCode)
     * Для расхода - источник подразделения - отправитель (senderCode)
     */
    const sourceDivisionForSeed = listType === BalanceDocType.Arrival ? "receiverCode" : "senderCode";
    const availableDivisionsSeed = useDivisionFilterSeed<WsInplantsFilterFormValues>(sourceDivisionForSeed, {
        filter: { AvailableFor: "Create" },
    });
    return (
        <FilterFormPanel
            initialFilter={initialFilter}
            seed={availableDivisionsSeed}
            submissionMemory={{
                key: "workShop.wsInplants.filter",
                schemaVersion: 1,
                fields: [sourceDivisionForSeed], // "stateCodes"
            }}
            formProps={{ formConfig: wsInplantsFilterFormConfig }}
            toForm={(filter) => wsInplantsFilterFormConfig.toForm(filter)}
            toDomain={(values) => wsInplantsFilterFormConfig.toDomain(values)}
            onApply={onApply}
        >
            {(form) => (
                <>
                    <BusinessForm.Field name="num">
                        <Input {...form.field("num").getPropsWithLabel()} />
                    </BusinessForm.Field>
                    <BusinessForm.Field name="senderCode">
                        <DivisionSearchSelect
                            {...form.field("senderCode").getSearchSelectProps((division) => division?.Code ?? null)}
                            flt={divisionFltForSender(listType)}
                        />
                    </BusinessForm.Field>
                    <BusinessForm.Field name="receiverCode">
                        <DivisionSearchSelect
                            {...form.field("receiverCode").getSearchSelectProps((division) => division?.Code ?? null)}
                            flt={divisionFltForReceiver(listType)}
                        />
                    </BusinessForm.Field>
                    <BusinessForm.Field name="leaveMoveDateFrom">
                        <Input.Date {...form.field("leaveMoveDateFrom").getDateInputProps()} />
                    </BusinessForm.Field>
                    <BusinessForm.Field name="leaveMoveDateTo">
                        <Input.Date {...form.field("leaveMoveDateTo").getDateInputProps()} />
                    </BusinessForm.Field>
                    <BusinessForm.Field name="arrivalMoveDateFrom">
                        <Input.Date {...form.field("arrivalMoveDateFrom").getDateInputProps()} />
                    </BusinessForm.Field>
                    <BusinessForm.Field name="arrivalMoveDateTo">
                        <Input.Date {...form.field("arrivalMoveDateTo").getDateInputProps()} />
                    </BusinessForm.Field>
                </>
            )}
        </FilterFormPanel>
    );
};
